import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Clock, User, Calendar, Share2 } from 'lucide-react'
import { useApp } from '../context/app-context'
import { t } from '../i18n'
import { articles } from '../data/articles'
import SEO from '../components/SEO'

export default function ArticleDetail() {
  const { id } = useParams()
  const { lang } = useApp()
  const isAr = lang === 'ar'
  const navigate = useNavigate()
  const BackArrow = isAr ? ArrowRight : ArrowLeft

  const article = articles.find((a) => a.id === id)

  if (!article) {
    return (
      <div className="not-found" dir={isAr ? 'rtl' : 'ltr'}>
        <h2>{isAr ? 'المقال غير موجود' : 'Article not found'}</h2>
        <Link to="/articles" className="btn btn-primary">
          {t(lang, 'backToArticles')}
        </Link>
      </div>
    )
  }

  const title = isAr ? article.titleAr : article.titleEn
  const content = isAr ? article.contentAr : article.contentEn
  const author = isAr ? article.authorAr : article.authorEn

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(isAr ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const related = articles.filter((a) => a.id !== id && a.category === article.category).slice(0, 3)

  const renderContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h3 key={i} className="article-body-h3">{line.slice(2, -2)}</h3>
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="article-body-li">{line.slice(2)}</li>
      }
      if (line.startsWith('✓') || line.startsWith('✅')) {
        return <li key={i} className="article-body-li check">{line}</li>
      }
      if (line.trim() === '') return <br key={i} />
      return <p key={i} className="article-body-p">{line}</p>
    })
  }

  return (
    <main className="page article-detail-page" dir={isAr ? 'rtl' : 'ltr'}>
      <SEO
        title={title}
        description={isAr ? article.excerptAr : article.excerptEn}
        url={`https://shelil-logistics-services.pages.dev/articles/${article.id}`}
        type="article"
        publishedTime={article.date}
        author={author}
        image={article.image}
      />
      <div className="article-detail-hero">
        <div className="article-detail-overlay" />
        <img src={article.image} alt={title} className="article-detail-hero-img" />
        <div className="article-detail-hero-content">
          <button
            className="back-btn"
            onClick={() => navigate('/articles')}
          >
            <BackArrow size={18} />
            {t(lang, 'backToArticles')}
          </button>
          <h1 className="article-detail-title">{title}</h1>
          <div className="article-detail-meta">
            <span><User size={15} />{author}</span>
            <span><Calendar size={15} />{formatDate(article.date)}</span>
            <span><Clock size={15} />{article.readTime} {t(lang, 'minRead')}</span>
          </div>
        </div>
      </div>

      <div className="article-detail-body">
        <div className="article-detail-content">
          {renderContent(content)}
        </div>

        <div className="article-share">
          <Share2 size={18} />
          <span>{t(lang, 'shareArticle')}</span>
          <button onClick={() => navigator.clipboard.writeText(window.location.href)} aria-label="Copy link" className="share-btn">Copy Link</button>
          <a href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-btn">WhatsApp</a>
        </div>

        {related.length > 0 && (
          <div className="related-articles">
            <h2>{isAr ? 'مقالات ذات صلة' : 'Related Articles'}</h2>
            <div className="related-grid">
              {related.map((ra) => (
                <div
                  key={ra.id}
                  className="related-card"
                  onClick={() => navigate(`/articles/${ra.id}`)}
                >
                  <img
                    src={ra.image}
                    alt={isAr ? ra.titleAr : ra.titleEn}
                    className="related-img"
                  />
                  <div className="related-info">
                    <h4>{isAr ? ra.titleAr : ra.titleEn}</h4>
                    <span>{ra.readTime} {t(lang, 'minRead')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  )
}
